import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { motion, MotionConfig } from "framer-motion";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Loader2, RotateCcw, RefreshCw, Search, X, ChevronLeft, ChevronRight } from "lucide-react";

import api from "../../chat/api";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import ReviewerActionGate from "../../../components/ReviewerActionGate";

// True for reviewers: Memory is shown, but every control that changes it is hidden or blocked.
const ReadOnly = createContext(false);

/* What the system believes about this company.
 *
 * This profile decides what proposals may claim and whether a new RFP looks like
 * a fit, and until now it was invisible — built by a background task, read inside
 * a prompt, reachable only from a shell. Nobody could see that a contractor's
 * capabilities included "Superintendent" (a job title), or that an organisation's
 * extraction had failed outright and left it with nothing.
 *
 * LAYOUT FOLLOWS THE CONTENT, NOT THE FIELD NAME. The profile mixes two-word
 * values ("Noida") with whole paragraphs (the QA process, the team summary) and
 * full sentences (every exclusion). Choosing a control per field would mean
 * guessing which is which, and the guess is wrong as soon as a rebuild produces
 * a longer answer — a one-line input showed the TAIL of a paragraph and nothing
 * else. So the row measures its own text: short values stay on one line, long
 * ones wrap and edit in a textarea, and a list of sentences renders as stacked
 * rows rather than as chips.
 *
 * EDITS ARE STORED SEPARATELY FROM THE EXTRACTION. A rebuild replaces the
 * extracted profile wholesale whenever the Knowledge Base changes, so an edit
 * written into it would survive until the next upload and then quietly revert.
 * The server keeps corrections in their own field and re-applies them over every
 * rebuild — which is why each value says whether it was read from a document or
 * typed by a person. Showing both identically would invite trusting the first as
 * much as the second.
 */

const URL = "/api/v1/rfp/organization-memory/";
const RESET_URL = "/api/v1/rfp/organization-memory/reset/";
const SYNC_URL = "/api/v1/rfp/organization-memory/sync/";

// While a rebuild is queued or running, the server can't tell us exactly when
// it'll land — the settle window alone is up to 5 minutes before the ~150s
// build even starts. Poll rather than guess a duration; 10s is frequent enough
// to feel responsive without hammering the endpoint over what can be several
// minutes of waiting.
const REBUILD_POLL_MS = 10000;

/* MOTION, KEPT TO THE THREE PLACES IT EARNS.
 *
 * The audience is mostly non-technical and often older, and the screen has been
 * cut back twice already to stay calm. So no decoration: motion here exists to
 * answer "what just changed?" — a panel that grows instead of appearing, a tab
 * pill that slides to where you clicked, content that settles in rather than
 * swapping under the cursor. Every duration is under a quarter of a second, and
 * the whole tree sits inside <MotionConfig reducedMotion="user"> so an OS
 * "reduce motion" setting turns all of it off without per-site checks.
 */
// A tab's content settling in: a short rise, no exit. Waiting for an outgoing
// panel to fade before the next appears would add a pause on every click, and a
// pause is the one thing a tab switch must not have.
const RISE = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.18, ease: "easeOut" },
};

// Past this, a value is prose: it wraps instead of truncating, and it edits in a
// textarea. Roughly one line of the value column at this width.
const LONG_TEXT = 88;
// A list whose typical item is this long is a list of sentences, not of labels.
const LONG_ITEM = 55;

const Chevron = ({ open }) => (
  <svg
    className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`}
    width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const PencilIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
  </svg>
);

// 24px box = WCAG 2.2 minimum target size; the negative margin keeps row height.
// Only used for edit and remove, so reviewers never see it.
const IconBtn = ({ label, onClick, disabled, danger = false, children }) => {
  const readOnly = useContext(ReadOnly);
  if (readOnly) return null;
  return (
    <button
      type="button" aria-label={label} title={label} disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`-my-[1.5px] inline-flex h-6 w-6 items-center justify-center rounded-md text-[#71717A] transition-colors disabled:opacity-40 ${
        danger ? "hover:bg-[#FEF2F2] hover:text-[#C0392B]" : "hover:bg-[#EFEFF1] hover:text-[#18181B]"
      }`}
    >
      {children}
    </button>
  );
};

/* The one editor, in two shapes.
 *
 * Short values keep the single-line input where Enter commits. Long ones get a
 * textarea that grows to its content — a paragraph in a one-line box shows its
 * last few words and hides the rest, which is exactly what made the Team field
 * unreadable. There Enter has to insert a newline, so the commit key moves to
 * Ctrl/Cmd+Enter and the row says so rather than leaving it to be discovered. */
const InlineEditor = ({ value, placeholder, multiline, label, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(value || "");
  const ref = useRef(null);
  const hintId = useId();

  const autosize = useCallback(() => {
    const el = ref.current;
    if (!el || !multiline) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [multiline]);

  useEffect(() => {
    ref.current?.focus();
    if (!multiline) ref.current?.select();
    autosize();
  }, [multiline, autosize]);

  const commit = () => {
    const t = draft.trim();
    if (!t || t === value) onCancel();
    else onCommit(t);
  };

  const shared = {
    ref,
    value: draft,
    placeholder,
    "aria-label": label,
    onChange: (e) => { setDraft(e.target.value); autosize(); },
    onClick: (e) => e.stopPropagation(),
    onBlur: commit,
    className:
      "w-full rounded-md border border-[#C95EBE]/45 bg-white px-2.5 py-[6px] text-[16.5px] leading-relaxed text-[#18181B] outline-none",
  };

  if (!multiline) {
    return (
      <input
        {...shared}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit();
          if (e.key === "Escape") onCancel();
        }}
      />
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <textarea
        {...shared}
        rows={3}
        aria-describedby={hintId}
        className={`${shared.className} resize-none`}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
        }}
      />
      <div id={hintId} className="mt-1 text-[14.5px] text-[#71717A]">
        Ctrl+Enter to save · Esc to cancel
      </div>
    </div>
  );
};

/* Same tag the proposal-writer prompt stamps on this exact entry (rfp.prompts.
 * kb._entry_tier) — shown here so a person reading Memory sees the same
 * confidence signal that governs what generation is allowed to claim.
 * "delivered" gets the one confident color; won/submitted read as equally
 * provisional, because that is exactly how the writer treats them — both are
 * "our position", never a stated fact.
 *
 * `claimed`/`scraped` are deliberately NOT in this map. Generation, chat and
 * Bid Analysis all now DROP an entry in either tier before it ever reaches a
 * prompt (see EVIDENCE_TIERS in rfp/services/kb_folders.py) rather than show
 * it hedged — so a badge reading "claimed" here would name a tier nothing
 * downstream still treats as evidence, and did exactly that: the same word
 * appeared under a section literally titled "Delivered work". The entry
 * itself still shows, for someone reviewing/correcting Memory, but with no
 * tier badge — same as an entry that traces to no document at all. */
const TIER_LABEL = { delivered: "Delivered", won: "Won", past_proposal: "Submitted" };
const TierTag = ({ tier }) => {
  if (!TIER_LABEL[tier]) return null;
  const strong = tier === "delivered";
  return (
    <span
      title={strong ? "Proven — may be stated as fact" : "Not yet proven — written as our position, not a stated fact"}
      className={`inline-flex flex-none items-center whitespace-nowrap rounded-full border px-2 py-[1px] text-[12px] font-medium ${
        strong ? "border-[#6DBA84] bg-[#E3F4E8] text-[#0B6B24]" : "border-[#A1A1AA] bg-[#F1F1F3] text-[#3F3F46]"
      }`}
    >
      {TIER_LABEL[tier] || tier}
    </span>
  );
};

// Shown on anything a person typed or corrected; the server keeps those over every rebuild.
const EditedTag = () => (
  <span
    title="Typed by a person — kept over every rebuild"
    className="inline-flex flex-none items-center whitespace-nowrap rounded-full border border-[#E7C6E3] bg-[#F7E9F5] px-2 py-[1px] text-[12px] font-medium text-[#6E2A67]"
  >
    Edited by you
  </span>
);

const CountPill = ({ n }) => (
  <span className="rounded-full bg-[#F4F4F5] px-[7px] text-[12px] font-semibold leading-[18px] tabular-nums text-[#3F3F46]">
    {n}
  </span>
);

const DocIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

/* An entry's detail, shown when it is opened.
 *
 * THE SOURCE LINE IS THE REASON THIS EXISTS. Every extracted entry records the
 * document and page it came from, and a capability records whether it is
 * `delivered` or only `claimed` — the same distinction the evidence ladder turns
 * on. The screen was showing the name alone, so it could tell you the company
 * claims a capability but not where the claim came from or whether anything backs
 * it, which is the one question a person reading this page has. */
/* One detail field. A closed vocabulary gets a select; anything else an input.
 *
 * These were read-only, which locked the wrong half: the headline was copied
 * verbatim out of a document and is usually right, while the classification
 * beside it — product or service, delivered or claimed, held or aligned — is
 * what a model guessed from context. "Aligned" and "certified" are the
 * difference between a true statement and a false claim in a compliance
 * section, so that is exactly the field a person needs to be able to correct. */
/* One field inside the item detail panel. The value sits in plain view with an
 * always-visible "Edit" link beside its label — clicking it turns just that
 * field into an editor, the same click-to-edit shape as the rest of Memory,
 * just inside a panel instead of squeezed into a hover-only row. */
const PanelField = ({ f, busy, onSet }) => {
  const [editing, setEditing] = useState(false);
  const readOnly = useContext(ReadOnly);
  const canEdit = f.editable && !readOnly;

  if (editing && f.choices?.length) {
    return (
      <div className="py-4">
        <div className="mb-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{f.label}</div>
        <select
          autoFocus
          aria-label={f.label}
          defaultValue={f.value}
          onChange={(e) => { setEditing(false); onSet(e.target.value); }}
          onBlur={() => setEditing(false)}
          className="w-full rounded-md border border-[#C95EBE]/45 bg-white px-2.5 py-[7px] text-[16px] text-[#18181B] outline-none"
        >
          <option value="">—</option>
          {f.choices.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    );
  }
  if (editing) {
    return (
      <div className="py-4">
        <div className="mb-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{f.label}</div>
        <InlineEditor
          value={f.value}
          label={f.label}
          multiline={f.value.length > LONG_TEXT}
          onCancel={() => setEditing(false)}
          onCommit={(t) => { setEditing(false); onSet(t); }}
        />
      </div>
    );
  }
  if (!f.value) {
    return (
      <div className="py-4">
        <div className="mb-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{f.label}</div>
        {canEdit ? (
          <button
            type="button" disabled={busy} onClick={() => setEditing(true)}
            className="text-[15.5px] font-medium text-[#C95EBE] transition-opacity hover:opacity-75 disabled:opacity-40"
          >
            + Add {f.label.toLowerCase()}
          </button>
        ) : (
          <span className="text-[15.5px] italic text-[#A1A1AA]">Not set</span>
        )}
      </div>
    );
  }
  return (
    <div className="py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{f.label}</div>
        {canEdit && (
          <button
            type="button" disabled={busy} onClick={() => setEditing(true)}
            aria-label={`Edit ${f.label}`}
            className="text-[15px] font-medium text-[#71717A] transition-colors hover:text-[#C95EBE] disabled:opacity-40"
          >
            Edit
          </button>
        )}
      </div>
      <div className="mt-1 whitespace-pre-wrap break-words text-[16.5px] leading-relaxed text-[#3F3F46]">
        {f.value}
      </div>
    </div>
  );
};

/* One confirm for every remove on this screen, in the app's own dialog rather
 * than the browser's: window.confirm() is unstyled, shows "localhost:3000 says",
 * and cannot be told apart from a system error. askConfirm() returns a promise
 * of true/false; <ConfirmHost /> (mounted once, below) draws the dialog. */
let confirmListener = null;
const askConfirm = (opts) =>
  new Promise((resolve) => {
    // No host mounted (should not happen) — fall back rather than skip the check.
    if (!confirmListener) { resolve(window.confirm(opts.message)); return; }
    confirmListener({ ...opts, resolve });
  });

function ConfirmHost() {
  const [req, setReq] = useState(null);
  useEffect(() => {
    confirmListener = setReq;
    return () => { confirmListener = null; };
  }, []);
  if (!req) return null;
  const close = (answer) => { req.resolve(answer); setReq(null); };
  return (
    <ConfirmDialog
      title={req.title}
      message={req.message}
      confirmLabel={req.confirmLabel || "Remove"}
      danger
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
}

const removeCopy = (name) => ({
  title: `Remove "${name}"?`,
  message: "It will be removed from Memory, and a rebuild won't add it back.",
});

const PERSON_LIST_FIELD = "team_members";

// These five fields carry a real record — several fields deep, each entry
// worth its own card — rather than a short label list like "Sectors we
// serve". They get the card grid; everything else keeps the plain chip/row
// list, where a big card per one-word entry would waste the whole screen.
const CARD_GRID_FIELDS = new Set([
  "team_members", "capabilities", "named_projects", "credentials_and_compliance",
  "commercial_models",
  // "tools_and_resources" removed 2026-10-01 while that feature is disabled
  // on the backend — see the TOOLS AND RESOURCES FEATURE comment block in
  // rfp/prompts/kb.py for the re-enable steps.
]);

// A one-off label per list field, so the footer reads "Remove person", not
// "Remove people" — the noun most of these fields are named by isn't the same
// as the noun for one of them.
const SINGULAR_NOUN = {
  team_members: "person",
  capabilities: "capability",
  named_projects: "project",
  credentials_and_compliance: "credential",
  // tools_and_resources: "resource",  // removed 2026-10-01, feature disabled.
  commercial_models: "model",
};

// The one field, per record type, that belongs beside the name as a subtitle
// rather than buried in the fields below — a capability's type, a project's
// client, a credential's type, a resource's type. team_members has no entry
// here because its headline already carries the role (see cardParts).
const CARD_SUBTITLE_FIELD = {
  capabilities: "type",
  named_projects: "client",
  credentials_and_compliance: "type",
  commercial_models: "basis",
  // tools_and_resources: "type",  // removed 2026-10-01, feature disabled.
};

// The one field, per record type, that SPLITS the grid into labelled groups
// (e.g. "Cloud & infrastructure" · 17, "AI & data" · 8) rather than one flat
// grid — tools_and_resources was the only field with this, and that feature
// is disabled 2026-10-01 (see rfp/prompts/kb.py's TOOLS AND RESOURCES
// FEATURE comment block). Left empty rather than deleted, same reason
// groupByCategory below still takes a general entryField argument.
const CARD_GROUP_FIELD = {};
// Items with no group value (or a field type with none configured) all land
// together, named plainly rather than left to read as the first "real" group.
const UNGROUPED_LABEL = "Other";

// Partitions items by their CARD_GROUP_FIELD value, in first-seen order, with
// anything ungrouped moved to the end. `items` carries each entry's ORIGINAL
// index in entry.items — openIdx/editingIdx key on that, not a position
// inside one group — so the index travels through the grouping, not the item
// alone. Returns [{ label, entries: [{ item, i }] }], or a single unlabelled
// bucket when entryField has no group field configured.
function groupByCategory(entryField, items) {
  const groupKey = CARD_GROUP_FIELD[entryField];
  if (!groupKey) return [{ label: null, entries: items }];
  const order = [];
  const byLabel = new Map();
  const ungrouped = [];
  for (const entry of items) {
    const value = (entry.item.detail?.fields || []).find((f) => f.key === groupKey)?.value?.trim();
    if (!value) { ungrouped.push(entry); continue; }
    if (!byLabel.has(value)) { byLabel.set(value, []); order.push(value); }
    byLabel.get(value).push(entry);
  }
  const groups = order.map((label) => ({ label, entries: byLabel.get(label) }));
  if (ungrouped.length) groups.push({ label: UNGROUPED_LABEL, entries: ungrouped });
  return groups.length ? groups : [{ label: null, entries: items }];
}

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// The card's name and subtitle. team_members' stored headline is already
// "Name — Role", so that splits apart directly; the other three record types
// fold their subtitle into a trailing "(...)" on the headline (see entry_text
// in org_memory.py) or carry no subtitle at all — so for them the name is the
// headline with that suffix stripped, and the subtitle comes straight from
// the matching field in CARD_SUBTITLE_FIELD instead of being parsed out.
function cardParts(entryField, item) {
  if (entryField === PERSON_LIST_FIELD) {
    const [name, ...rest] = String(item.text || "").split(" — ");
    // The Role field wins over the headline's copy of it: the headline is
    // rebuilt on the server, the field updates the moment it's edited.
    const role = (item.detail?.fields || []).find((f) => f.key === "designation")?.value;
    return { name: name.trim(), subtitle: role || rest.join(" — ").trim() };
  }
  const name = String(item.text || "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim() || item.text;
  const subKey = CARD_SUBTITLE_FIELD[entryField];
  const subtitle = subKey
    ? (item.detail?.fields || []).find((f) => f.key === subKey)?.value || null
    : null;
  return { name, subtitle };
}

/* "+ Add detail": a new label AND its value, for records whose useful detail
 * is not the same every time (a credential's issuer, number, scope). Saved as a
 * "facet:<label>" field, the same shape the extracted details already use. */
const AddDetail = ({ busy, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const labelRef = useRef(null);

  useEffect(() => { if (open) labelRef.current?.focus(); }, [open]);

  const reset = () => { setOpen(false); setLabel(""); setValue(""); };
  const commit = () => {
    const lb = label.trim();
    const v = value.trim();
    if (!lb || !v) return;
    onAdd(lb, v);
    reset();
  };
  const fieldCls =
    "block w-full border-0 border-b border-black/[0.12] bg-transparent px-0 py-1 text-[16px] text-[#18181B] outline-none placeholder:text-[#A1A1AA] focus:border-[#C95EBE]";

  if (!open) {
    return (
      <div className="py-4">
        <button
          type="button" disabled={busy} onClick={() => setOpen(true)}
          className="text-[15.5px] font-medium text-[#C95EBE] transition-opacity hover:opacity-75 disabled:opacity-40"
        >
          + Add detail
        </button>
      </div>
    );
  }
  return (
    <div
      className="py-4"
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); reset(); }
      }}
    >
      <datalist id="detail-label-suggestions">
        {["Issued by", "Certificate / registration number", "Scope", "Issued on", "Controls covered"].map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <div className="mb-1 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">Label</div>
      <input
        ref={labelRef} list="detail-label-suggestions" value={label} maxLength={60}
        onChange={(e) => setLabel(e.target.value)}
        aria-label="Detail label" placeholder="e.g. Issued by"
        className={fieldCls}
      />
      <div className="mb-1 mt-4 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">Value</div>
      <input
        value={value} maxLength={400}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Detail value" placeholder="Value"
        className={fieldCls}
      />
      <div className="mt-4 flex items-center gap-4">
        <button
          type="button" onClick={commit} disabled={!label.trim() || !value.trim()}
          className="rounded-lg bg-[#C95EBE] px-3.5 py-1.5 text-[15px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          Add
        </button>
        <button
          type="button" onClick={reset}
          className="text-[15px] font-medium text-[#71717A] transition-colors hover:text-[#18181B]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

/* The whole record, one field at a time, in a panel docked to the right edge
 * rather than a centered dialog — it reads as a detail view of the list
 * you're still looking at, not an interruption over it. Prev/Next move
 * between records in the same list without closing it. */
const ItemDetailPanel = ({
  entryField, entryLabel, item, index, count, orgId, busy, allowFacets,
  onSetField, onRemove, onClose, onPrev, onNext,
}) => {
  const readOnly = useContext(ReadOnly);
  const detail = item.detail || {};
  const fields = detail.fields || [];
  const source = detail.source;

  useEffect(() => {
    const onKey = (e) => {
      // A field handled this key already (Esc cancelling an open editor), or the
      // person is typing — arrows move the caret there, not between records.
      if (e.defaultPrevented) return;
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || "");
      if (e.key === "Escape") onClose();
      if (typing) return;
      if (e.key === "ArrowLeft" && index > 0) onPrev();
      if (e.key === "ArrowRight" && index < count - 1) onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, index, count]);

  const isCardGrid = CARD_GRID_FIELDS.has(entryField);
  const { name, subtitle } = isCardGrid ? cardParts(entryField, item) : { name: item.text, subtitle: null };
  const noun = SINGULAR_NOUN[entryField] || entryLabel.toLowerCase().replace(/s$/, "");

  return (
    <div className="fixed inset-0 z-[10000] flex justify-end bg-black/20" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-[480px] flex-col bg-white font-outfit shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={item.text}
      >
        <div className="flex items-center gap-3.5 px-6 py-5">
          {isCardGrid && (
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#C95EBE]/[0.14] text-[15px] font-semibold text-[#C95EBE]">
              {initialsOf(name)}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[18px] font-semibold text-[#18181B]">{name}</div>
            {subtitle && <div className="truncate text-[15.5px] text-[#71717A]">{subtitle}</div>}
          </div>
          {count > 1 && (
            <div className="flex flex-none items-center gap-0.5 text-[15px] text-[#71717A]">
              <button
                type="button" onClick={onPrev} disabled={index <= 0} aria-label={`Previous ${noun}`}
                className="rounded-md p-1.5 transition-colors hover:bg-black/[0.05] hover:text-[#18181B] disabled:opacity-30"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="tabular-nums">{index + 1}/{count}</span>
              <button
                type="button" onClick={onNext} disabled={index >= count - 1} aria-label={`Next ${noun}`}
                className="rounded-md p-1.5 transition-colors hover:bg-black/[0.05] hover:text-[#18181B] disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
          <button
            type="button" onClick={onClose} aria-label="Close"
            className="flex-none rounded-md p-1.5 text-[#71717A] transition-colors hover:bg-black/[0.05] hover:text-[#18181B]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="h-px flex-none bg-black/[0.07]" />

        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          {source && (
            <div className="flex items-center gap-1.5 pt-3 text-[14.5px] text-[#71717A]">
              <DocIcon />
              <span className="min-w-0 truncate">
                {source.doc}{source.page ? ` · p.${source.page}` : ""}
              </span>
            </div>
          )}
          <div className="divide-y divide-black/[0.06]">
            {fields.map((f) => (
              <PanelField key={f.key || f.label} f={f} busy={busy}
                         onSet={(v) => onSetField(f.key, v)} />
            ))}
          </div>
          {allowFacets && !readOnly && (
            <div className={fields.length ? "border-t border-black/[0.06]" : ""}>
              <AddDetail busy={busy} onAdd={(lb, v) => onSetField(`facet:${lb}`, v)} />
            </div>
          )}
          {!fields.length && (!allowFacets || readOnly) && (
            <p className="py-6 text-[15.5px] text-[#71717A]">
              Nothing more to fill in for this {noun}.
            </p>
          )}
        </div>

        <div className="flex flex-none items-center justify-between gap-3 border-t border-black/[0.07] px-6 py-4">
          {readOnly ? <span /> : (
            <button
              type="button" disabled={busy}
              onClick={async () => { if (await askConfirm(removeCopy(name))) onRemove(); }}
              className="text-[15.5px] font-medium capitalize text-[#C0392B] transition-opacity hover:opacity-75 disabled:opacity-40"
            >
              Remove {noun}
            </button>
          )}
          <button
            type="button" onClick={onClose}
            className="rounded-lg bg-[#C95EBE] px-4 py-2 text-[15.5px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* One value inside an expanded list. Short values are chips; sentences are rows,
   because a 180-character exclusion in a chip is a truncated fragment.

   An entry that carries detail is clickable and says so with a chevron; one that
   does not stays inert, so a click never opens an empty panel.

   The panel itself lives one level up, at ListRow — Prev/Next needs the whole
   items array and a shared index, not one this component can own alone. */
const ListItem = ({ item, asRow, busy, onEdit, onRemove, onOpen }) => {
  const hasDetail = !!(item.detail?.fields?.length || item.detail?.source);
  // Same look whether extracted or typed by hand: an edit is a remove plus an
  // add, so tinting "added" turned every corrected chip pink.
  const tint = "border-[#A1A1AA] bg-white text-[#27272A]";

  // Rows keep room for the icons; chips only make room while hovered, so they stay snug.
  const controls = (
    <span className={`flex-none items-center ${asRow
      ? "flex opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      : "hidden group-hover:flex group-focus-within:flex"}`}>
      <IconBtn label="Edit" disabled={busy} onClick={onEdit}><PencilIcon /></IconBtn>
      <IconBtn
        danger label="Remove" disabled={busy}
        onClick={async () => { if (await askConfirm(removeCopy(item.text))) onRemove(); }}
      >
        <TrashIcon />
      </IconBtn>
    </span>
  );

  if (asRow) {
    // The chevron's padding grows its hit area to 24px; the matching negative
    // margin leaves the icon exactly where it was.
    return (
      <div className={`group rounded-lg border px-3 py-[7px] ${tint}`}>
        <div className="flex items-start gap-2">
          {hasDetail && (
            <button
              type="button"
              onClick={onOpen}
              aria-haspopup="dialog"
              aria-label={`Open ${item.text}`}
              className="-mx-[5px] -mb-[5px] -mt-[1px] flex-none rounded-md p-[5px] text-[#71717A] transition-colors hover:text-[#18181B]"
            >
              <Chevron open={false} />
            </button>
          )}
          <span
            onClick={hasDetail ? onOpen : undefined}
            className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-[14px] leading-relaxed ${hasDetail ? "cursor-pointer" : ""}`}
          >
            {item.text}
          </span>
          <TierTag tier={item.tier} />
          {(item.origin === "added" || item.patched) && <EditedTag />}
          {controls}
        </div>
      </div>
    );
  }

  // The real <button> is what keyboard and screen-reader users operate; the
  // chip-wide onClick only widens the mouse target. Edit/Remove stay siblings
  // of the button because interactive elements cannot nest.
  return (
    <span
      onClick={hasDetail ? onOpen : undefined}
      className={`group inline-flex max-w-full items-center gap-1.5 rounded-full border py-[3px] pl-3 pr-1.5 text-[14px] ${tint} ${
        hasDetail ? "cursor-pointer hover:border-[#A04A97] focus-within:border-[#A04A97]" : ""
      }`}
    >
      {hasDetail ? (
        <button
          type="button"
          aria-haspopup="dialog"
          className="inline-flex min-w-0 items-center gap-1 rounded-sm text-left"
        >
          <span className="truncate">{item.text}</span>
          <TierTag tier={item.tier} />
          <span aria-hidden className="flex-none text-[#71717A] group-hover:text-[#A04A97]">
            <Chevron open={false} />
          </span>
        </button>
      ) : (
        <>
          <span className="truncate">{item.text}</span>
          <TierTag tier={item.tier} />
        </>
      )}
      {controls}
    </span>
  );
};

/* One record in a card grid (a person, a capability, a project, a credential)
   — a monogram, its name and subtitle, and how much of the record is still
   blank, so a gap is visible from the list view rather than discovered one
   entry at a time. */
const EntryCard = ({ entryField, item, busy, onOpen, onEdit, onRemove }) => {
  const { name, subtitle } = cardParts(entryField, item);
  const fields = item.detail?.fields || [];
  const missing = fields.filter((f) => f.editable && !f.value).length;
  const source = item.detail?.source;
  const edited = item.origin === "added" || item.patched;
  return (
    <div className="group relative flex min-w-0 flex-col gap-2.5 rounded-lg border border-[#D4D4D8] bg-white px-3.5 py-3 transition-colors hover:border-[#A04A97] hover:bg-[#FDF8FC]">
      <button
        type="button" onClick={onOpen}
        aria-haspopup="dialog"
        className="flex w-full min-w-0 items-start gap-2.5 text-left"
      >
        {entryField === PERSON_LIST_FIELD && (
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#F7E9F5] text-[13px] font-semibold text-[#A04A97]">
            {initialsOf(name)}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 pr-12">
          <span className="line-clamp-2 text-[14px] font-medium leading-snug text-[#18181B]">{name}</span>
          <span className="truncate text-[13px] text-[#3F3F46]">{subtitle || "—"}</span>
        </span>
      </button>
      <div className="flex min-w-0 items-center gap-1.5">
        <TierTag tier={item.tier} />
        {edited && <EditedTag />}
        {missing > 0 && (
          <span className="whitespace-nowrap text-[12px] text-[#71717A]">
            {missing} {missing === 1 ? "detail" : "details"} missing
          </span>
        )}
        <span className="flex-1" />
        <span
          title={source?.doc ? `${source.doc}${source.page ? `, page ${source.page}` : ""}` : undefined}
          className="min-w-0 truncate text-[12px] text-[#52525B]"
        >
          {source?.doc || (item.origin === "added" ? "Added by you" : "")}
        </span>
      </div>
      <span className="absolute right-2 top-2 flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <IconBtn label={`Edit ${name}`} disabled={busy} onClick={onEdit}><PencilIcon /></IconBtn>
        <IconBtn
          danger label={`Remove ${name}`} disabled={busy}
          onClick={async () => { if (await askConfirm(removeCopy(name))) onRemove(); }}
        >
          <TrashIcon />
        </IconBtn>
      </span>
    </div>
  );
};

/* One list field: a single collapsed row, expandable in place. The four
   record-shaped fields (see CARD_GRID_FIELDS) render as a card grid; every
   other list keeps the plain chip/row layout. Either way, opening an entry
   opens ONE shared detail panel (openIdx lives here, not per-item) so
   Prev/Next can walk the whole list. */
/* The "add" form for a record with a fixed shape: the SAME panel as the detail
 * view, built from the same PanelField / AddDetail pieces, so the two cannot
 * drift apart — every field is the pink "+ Add …" row, the name is the title.
 * Nothing is saved until Save (the old flow stored a bare name first).
 *
 * Save is the add plus one set_item_field per filled field. mutate() queues
 * them in order, and the field patches key on the name, so they land on the
 * record the add just created. */
const NewItemPanel = ({ entry, noun, onSave, onCancel }) => {
  const [name, setName] = useState("");
  const [values, setValues] = useState({});
  const [details, setDetails] = useState([]);
  const [error, setError] = useState("");
  const nameRef = useRef(null);
  const schema = entry.item_schema || [];

  useEffect(() => { nameRef.current?.focus(); }, []);

  const save = () => {
    const title = name.trim();
    if (!title) { setError(`Give this ${noun} a name first.`); nameRef.current?.focus(); return; }
    if (entry.items.some((i) => i.key.trim().toLowerCase() === title.toLowerCase())) {
      setError(`There is already a ${noun} called "${title}".`);
      nameRef.current?.focus();
      return;
    }
    const filled = {};
    schema.forEach((f) => {
      const v = (values[f.key] || "").trim();
      if (v) filled[f.key] = v;
    });
    details.forEach(({ label, value }) => { filled[`facet:${label}`] = value; });
    onSave(title, filled);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  return (
    <div className="fixed inset-0 z-[10000] flex justify-end bg-black/20" onClick={onCancel}>
      <div
        className="flex h-full w-full max-w-[480px] flex-col bg-white font-outfit shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Add ${noun}`}
      >
        <div className="flex items-center gap-3.5 px-6 py-5">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#C95EBE]/[0.14] text-[15px] font-semibold text-[#C95EBE]">
            {name.trim() ? initialsOf(name) : "+"}
          </span>
          <div className="min-w-0 flex-1">
            <input
              ref={nameRef} value={name} maxLength={400}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              aria-label={`${noun} name`}
              aria-invalid={!!error}
              aria-describedby={error ? "new-item-error" : undefined}
              placeholder={`Name of the ${noun}`}
              className="block w-full truncate border-0 bg-transparent p-0 text-[18px] font-semibold text-[#18181B] outline-none placeholder:font-semibold placeholder:text-[#71717A]"
            />
          </div>
          <button
            type="button" onClick={onCancel} aria-label="Close"
            className="flex-none rounded-md p-1.5 text-[#71717A] transition-colors hover:bg-black/[0.05] hover:text-[#18181B]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="h-px flex-none bg-black/[0.07]" />

        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          {error && (
            <p id="new-item-error" role="alert" className="pt-3 text-[14.5px] text-[#C0392B]">{error}</p>
          )}
          <div className="divide-y divide-black/[0.06]">
            {schema.map((f) => (
              <PanelField
                key={f.key}
                f={{ ...f, value: values[f.key] || "", editable: true }}
                busy={false}
                onSet={(v) => setValues((cur) => ({ ...cur, [f.key]: v }))}
              />
            ))}
            {details.map((d, i) => (
              <PanelField
                key={`detail-${d.label}`}
                f={{ key: `facet:${d.label}`, label: d.label, value: d.value, editable: true, choices: [] }}
                busy={false}
                onSet={(v) => setDetails((cur) => cur.map((row, j) => (j === i ? { ...row, value: v } : row)))}
              />
            ))}
          </div>
          {entry.allow_facets && (
            <div className="border-t border-black/[0.06]">
              <AddDetail
                busy={false}
                onAdd={(label, value) =>
                  setDetails((cur) => [...cur.filter((d) => d.label !== label), { label, value }])
                }
              />
            </div>
          )}
        </div>

        <div className="flex flex-none items-center justify-between gap-3 border-t border-black/[0.07] px-6 py-4">
          <button
            type="button" onClick={onCancel}
            className="text-[15.5px] font-medium text-[#71717A] transition-colors hover:text-[#18181B]"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save}
            className="rounded-lg bg-[#C95EBE] px-4 py-2 text-[15.5px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Save {noun}
          </button>
        </div>
      </div>
    </div>
  );
};

const ListRow = ({ entry, busy, orgId, onAdd, onRemove, onEdit, onSetField }) => {
  const [adding, setAdding] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [openIdx, setOpenIdx] = useState(-1);
  const isCardGrid = CARD_GRID_FIELDS.has(entry.field);
  const noun = SINGULAR_NOUN[entry.field] || entry.label.toLowerCase().replace(/s$/, "");

  // Measured off the data, so a field that starts short and later holds
  // sentences changes shape on its own.
  const longItems = useMemo(() => {
    if (!entry.items.length) return false;
    const avg =
      entry.items.reduce((n, i) => n + i.text.length, 0) / entry.items.length;
    return avg > LONG_ITEM;
  }, [entry.items]);

  const openItem = openIdx >= 0 ? entry.items[openIdx] : null;

  const readOnly = useContext(ReadOnly);
  const startAdd = () => (isCardGrid && entry.item_schema ? setDrafting(true) : setAdding(true));

  return (
    <div className="mb-4 flex flex-col gap-2">
      {/* Always expanded, like every other card — a list this important is not
          something a person should have to open on every visit to see. */}
      <div className="flex items-center gap-2">
        <span className="text-[15px] font-semibold text-[#18181B]">{entry.label}</span>
        <CountPill n={entry.items.length} />
        <span className="flex-1" />
        {!readOnly && <button
          type="button" disabled={busy} onClick={startAdd}
          aria-label={isCardGrid ? `Add ${noun}` : `Add to ${entry.label}`}
          className="text-[13px] font-semibold text-[#A04A97] hover:underline disabled:opacity-40"
        >
          {SINGULAR_NOUN[entry.field] ? `+ Add ${noun}` : "+ Add"}
        </button>}
      </div>

      <div>
        {isCardGrid && !entry.items.length && !adding ? (
          <p className="rounded-lg border border-dashed border-[#D4D4D8] px-3.5 py-4 text-[14px] text-[#71717A]">
            No {entry.label.toLowerCase()} yet.
          </p>
        ) : isCardGrid ? (
          groupByCategory(entry.field, entry.items.map((item, i) => ({ item, i }))).map((group, gi, groups) => (
            <div key={group.label ?? "__flat"}>
              {/* Only rendered for fields with a CARD_GROUP_FIELD (tools_and_resources
                  today) — everything else gets its single bucket with label: null
                  and this heading never appears, so their layout is unchanged. */}
              {group.label && (
                <div className={`${gi === 0 ? "" : "pt-3"} pb-2`}>
                  <div className="text-[14px] font-semibold text-[#18181B]">{group.label}</div>
                  <div className="text-[12px] text-[#3F3F46]">
                    {group.entries.length} {group.entries.length === 1 ? "item" : "items"}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2.5">
                {group.entries.map(({ item, i }) =>
                  editingIdx === i ? (
                    <div key={`e-${i}`} className="col-span-full">
                      <InlineEditor
                        value={item.text}
                        label={`Edit ${noun}`}
                        onCancel={() => setEditingIdx(-1)}
                        onCommit={(t) => { setEditingIdx(-1); onEdit(item.text, t); }}
                      />
                    </div>
                  ) : (
                    <EntryCard
                      key={`${item.text}-${i}`}
                      entryField={entry.field}
                      item={item}
                      busy={busy}
                      onOpen={() => setOpenIdx(i)}
                      onEdit={() => setOpenIdx(i)}
                      onRemove={() => onRemove(item.text)}
                    />
                  )
                )}
                {/* The add affordance always trails the LAST group's grid, never
                    floats as its own section — adding a resource is not itself
                    a category. */}
                {gi === groups.length - 1 && (
                  adding && !entry.item_schema && (
                    <div className="col-span-full">
                      <InlineEditor
                        value=""
                        label={`Add ${noun}`}
                        placeholder={entry.field !== "credentials_and_compliance"
                          ? `Type a name, then fill in the rest`
                          : undefined}
                        onCancel={() => setAdding(false)}
                        onCommit={async (t) => {
                          setAdding(false);
                          // People/Capabilities/Projects/Resources are stored as
                          // a small record ({"name": t}), not a bare string,
                          // exactly so the rest of it can be filled in right
                          // away — jump straight to that record's panel instead
                          // of leaving just a title behind.
                          const res = await onAdd(t);
                          const freshEntry = res?.sections
                            ?.flatMap((s) => s.entries)
                            .find((e) => e.field === entry.field);
                          const idx = freshEntry?.items.findIndex(
                            (it) => it.text.trim().toLowerCase() === t.trim().toLowerCase()
                          );
                          if (idx != null && idx >= 0 && freshEntry.items[idx].detail?.fields?.length) {
                            setOpenIdx(idx);
                          }
                        }}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-lg border border-[#D4D4D8] bg-white px-3.5 py-3">
            <div className={longItems ? "flex flex-col gap-2" : "flex flex-wrap items-start gap-2"}>
              {entry.items.map((item, i) =>
                editingIdx === i ? (
                  <div key={`e-${i}`} className="w-full max-w-[860px]">
                    <InlineEditor
                      value={item.text}
                      label={`Edit ${entry.label} entry`}
                      multiline={item.text.length > LONG_TEXT}
                      onCancel={() => setEditingIdx(-1)}
                      onCommit={(t) => { setEditingIdx(-1); onEdit(item.text, t); }}
                    />
                  </div>
                ) : (
                  <ListItem
                    key={`${item.text}-${i}`}
                    item={item}
                    asRow={longItems}
                    busy={busy}
                    onEdit={() => setEditingIdx(i)}
                    onRemove={() => onRemove(item.text)}
                    onOpen={() => setOpenIdx(i)}
                  />
                )
              )}
            </div>

            {adding && (
              <div className="mt-2 w-full max-w-[860px]">
                <InlineEditor
                  value=""
                  label={`Add to ${entry.label}`}
                  multiline={longItems}
                  placeholder="Type a fact and save"
                  onCancel={() => setAdding(false)}
                  onCommit={(t) => { setAdding(false); onAdd(t); }}
                />
              </div>
            )}
            {!entry.items.length && !adding && (
              <p className="text-[14px] text-[#71717A]">Nothing here yet.</p>
            )}
          </div>
        )}
      </div>

      {drafting && (
        <NewItemPanel
          entry={entry}
          noun={noun}
          onCancel={() => setDrafting(false)}
          onSave={(title, filled) => {
            setDrafting(false);
            onAdd(title);
            Object.entries(filled).forEach(([key, value]) => onSetField(title, key, value));
          }}
        />
      )}

      {openItem && (
        <ItemDetailPanel
          entryField={entry.field}
          entryLabel={entry.label}
          item={openItem}
          index={openIdx}
          count={entry.items.length}
          orgId={orgId}
          busy={busy}
          allowFacets={!!entry.allow_facets}
          onSetField={(key, value) => onSetField(openItem.key, key, value)}
          onRemove={() => { onRemove(openItem.text); setOpenIdx(-1); }}
          onClose={() => setOpenIdx(-1)}
          onPrev={() => setOpenIdx((i) => Math.max(0, i - 1))}
          onNext={() => setOpenIdx((i) => Math.min(entry.items.length - 1, i + 1))}
        />
      )}
    </div>
  );
};

/* One single-value fact: a cell in a two-column fact sheet.
 *
 * Deleting a value is not the same as blanking it: the server stores the empty
 * explicitly, because a rebuild would otherwise re-extract the very fact the
 * user just removed and they would have to remove it again after every upload.
 *
 * THE VALUE CARRIES THE WEIGHT, not the label. People come here to find "what
 * is our registration number", so the answer is the dark, larger text and the
 * label is the quiet caption above it. Long values span both columns — a
 * paragraph squeezed into half a row is harder to read than one that wraps. */
const FactCell = ({ entry, busy, onSet, onClear }) => {
  const [editing, setEditing] = useState(false);
  const readOnly = useContext(ReadOnly);
  const long = (entry.text || "").length > LONG_TEXT;

  return (
    <div className="group grid grid-cols-[180px_minmax(0,1fr)_auto] items-start gap-3 border-t border-[#E4E4E7] px-3.5 py-[11px] transition-colors first:border-t-0 hover:bg-[#FDFBFE] focus-within:bg-[#FDFBFE]">
      <div className="pt-px text-[14px] text-[#3F3F46]">{entry.label}</div>
      <div className="min-w-0">
        {editing ? (
          <InlineEditor
            value={entry.text}
            label={entry.label}
            multiline={long}
            onCancel={() => setEditing(false)}
            onCommit={(t) => { setEditing(false); onSet(t); }}
          />
        ) : entry.text ? (
          <span className="block whitespace-pre-wrap break-words text-[14px] leading-normal text-[#18181B]">
            {entry.text}
          </span>
        ) : readOnly ? (
          <span className="text-[14px] text-[#A1A1AA]">Not set</span>
        ) : (
          <button
            type="button" disabled={busy} onClick={() => setEditing(true)}
            aria-label={`Add ${entry.label}`}
            className="text-[14px] font-medium text-[#A04A97] hover:underline disabled:opacity-40"
          >
            + Add {entry.label.toLowerCase()}
          </button>
        )}
      </div>
      <div className="flex min-w-0 items-center justify-end gap-2">
        {!editing && entry.text && entry.edited && <EditedTag />}
        {!editing && entry.text && (
          <span className="flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <IconBtn label={`Edit ${entry.label}`} disabled={busy} onClick={() => setEditing(true)}>
              <PencilIcon />
            </IconBtn>
            <IconBtn
              danger label={`Remove ${entry.label}`} disabled={busy}
              onClick={async () => { if (await askConfirm(removeCopy(entry.label))) onClear(); }}
            >
              <TrashIcon />
            </IconBtn>
          </span>
        )}
      </div>
    </div>
  );
};


/* A section becomes a stack of CARDS. Top-level facts (no group) collect into
   one "Basics" card; each named group of facts (Legal identity, Financials…)
   is its own card; each list field (People, Capabilities…) is its own card too.
   Server order is otherwise kept, so a section still leads with what it led with. */
function toCards(entries) {
  const isFact = (e) => e.kind !== "list";
  const cards = [];
  const summary = entries.filter((e) => !e.group && isFact(e));
  if (summary.length) cards.push({ key: "basics", title: "Basics", kind: "fact", entries: summary });
  let group = null;
  for (const e of entries) {
    if (e.kind === "list") {
      cards.push({ key: e.field, title: e.label, kind: "list", entries: [e] });
      group = null;
      continue;
    }
    if (!e.group) { group = null; continue; }
    if (group && group.title === e.group) {
      group.entries.push(e);
    } else {
      group = { key: e.field, title: e.group, kind: "fact", entries: [e] };
      cards.push(group);
    }
  }
  return cards;
}

/* A titled card: uppercase group name in the header, rows beneath. */
function MemoryCard({ title, count, children }) {
  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-[15px] font-semibold text-[#18181B]">{title}</h3>
        {count != null && <CountPill n={count} />}
      </div>
      <div className="overflow-hidden rounded-lg border border-[#D4D4D8] bg-white">{children}</div>
    </div>
  );
}

/* What closing a project wrote down.
 *
 * THE HALF OF MEMORY NOBODY COULD SEE. Everything above this is distilled from
 * documents and can be corrected in place. This is written by the close hook —
 * a record of a decision somebody made, not a claim about the company — so it is
 * listed, not edited: correcting it means reopening the project and changing its
 * status, which is where that fact lives.
 *
 * The line that earns this section is `recall_note`. An outcome only reaches
 * another bid if its reason says something about capability AND it has something
 * to be matched by. A price loss is stored and deliberately never surfaced
 * elsewhere, and somebody who closed one would otherwise conclude the feature is
 * broken. Saying so here is cheaper than that conclusion.
 */

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

const officeAddress = (a) =>
  a && typeof a === "object"
    ? [a.name, a.city, a.state, a.zip].filter(Boolean).join(", ")
    : a || "";

/* One labelled fact, as a tile: a caption over the value. */
function Field({ label, value, sub }) {
  if (!value) return null;
  return (
    <div className="min-w-0 rounded-lg border border-black/[0.06] bg-[#FAFAFA] px-3.5 py-3">
      <dt className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">
        {label}
      </dt>
      <dd className="mt-1 break-words text-[16px] font-medium leading-snug text-[#18181B]">
        {value}
      </dd>
      {sub && <div className="mt-0.5 text-[14.5px] leading-snug text-[#71717A]">{sub}</div>}
    </div>
  );
}

/* A titled block inside a bid panel — the same caption-over-content rhythm as a
 * field in the item panels, with a divider between blocks. */
function PanelSection({ label, children }) {
  return (
    <section className="border-t border-black/[0.06] py-5 first:border-t-0 first:pt-0">
      <h3 className="mb-2.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">
        {label}
      </h3>
      {children}
    </section>
  );
}

/* The listing exactly as Tender Search held it, frozen with the bid.
 *
 * WHY KEEP IT AT ALL. SAM.gov removes a notice after its archive date, so the
 * source of a bid you closed six months ago may simply be gone — and with it the
 * solicitation number, the closing date and the contact you dealt with. A record
 * of a decision is not much use once the thing it was about is unreachable.
 */
function TenderDetails({ t }) {
  const codes = (t.classifications || []).filter((c) => c.code);
  const files = [
    t.attachment_count ? `${t.attachment_count} file${t.attachment_count === 1 ? "" : "s"}` : null,
    t.link_count ? `${t.link_count} web link${t.link_count === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  const address = officeAddress(t.office_address);

  return (
    <div>
      {t.buyer_path && (
        <p className="mb-5 text-[15.5px] leading-relaxed text-[#71717A]">{t.buyer_path}</p>
      )}

      <PanelSection label="Key facts">
        <dl className="grid grid-cols-2 gap-2.5">
          <Field label="Solicitation no." value={t.reference} />
          <Field label="Notice type" value={t.notice_type_label} />
          <Field label="Posted" value={fmtDate(t.published_at)} />
          <Field
            label="Closed"
            // The source publishes a closing time with no timezone on a quarter of
            // notices, so the date alone is all we can vouch for.
            value={fmtDate(t.closes_at) || "Not published"}
            sub={t.deadline_precision === "datetime" && t.closes_at_tz
              ? `${new Date(t.closes_at).toLocaleTimeString(undefined, {
                  hour: "numeric", minute: "2-digit" })} ${t.closes_at_tz}`
              : null}
          />
          <Field label="Listed until" value={fmtDate(t.archived_at)} />
          <Field
            label="Set aside"
            value={t.set_aside_label || t.set_aside_code || "Open to all bidders"}
          />
          <Field label="Place of performance" value={t.place || t.place_country} />
          {codes.map((c) => (
            <Field
              key={`${c.scheme}-${c.code}`}
              label={c.scheme_label}
              value={c.code}
              sub={c.label || c.group}
            />
          ))}
          <Field
            label="Industry"
            value={(t.categories || []).map((c) => c.label).join(", ")}
          />
          <Field label="Attachments" value={files.join(" · ")} />
          <Field
            label="Source"
            value={t.source_label}
            sub={t.last_confirmed_at ? `Confirmed ${fmtDate(t.last_confirmed_at)}` : null}
          />
        </dl>
      </PanelSection>

      {t.description && (
        <PanelSection label="Description">
          <p className="text-[16px] leading-[1.7] text-[#3F3F46]">{t.description}</p>
        </PanelSection>
      )}

      {(!!t.contacts?.length || address) && (
        <PanelSection label="Contact">
          <div className="space-y-3">
            {t.contacts?.map((c, i) => (
              <div key={i}>
                <p className="text-[16px] font-medium text-[#18181B]">{c.name || c.email}</p>
                <p className="text-[15px] leading-relaxed text-[#71717A]">
                  {[c.title, c.email, c.phone].filter(Boolean).join(" · ")}
                </p>
              </div>
            ))}
            {/* A dict from the adapter — {name, city, state, zip} — not a string.
                officeAddress flattens it; rendering the dict straight would throw
                "Objects are not valid as a React child" and take the whole Memory
                screen down with it. */}
            {address && <p className="text-[15px] leading-relaxed text-[#71717A]">{address}</p>}
          </div>
        </PanelSection>
      )}

      {t.source_url && (
        <a
          href={t.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-[#C95EBE]/40 px-3.5 py-2 text-[15.5px] font-medium text-[#9C3D93] no-underline transition-colors hover:bg-[#C95EBE]/[0.06]"
        >
          View on {t.source_label || "source"} ↗
        </a>
      )}
    </div>
  );
}

function Bullets({ label, items, marker = null, footer = null }) {
  if (!items?.length) return null;
  return (
    <PanelSection label={label}>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2.5 text-[16px] leading-relaxed text-[#3F3F46]">
            {marker ? (
              <span aria-hidden="true" className="mt-[1px] w-3.5 flex-none text-center text-[14px] text-[#71717A]">{marker}</span>
            ) : (
              <span aria-hidden="true" className="mt-[10px] h-[5px] w-[5px] flex-none rounded-full bg-[#C95EBE]/60" />
            )}
            <span className="min-w-0 flex-1">{it}</span>
          </li>
        ))}
      </ul>
      {footer}
    </PanelSection>
  );
}

/* What it was scored on, with the result beside each criterion.
 *
 * ONE LIST, NOT TWO. "Scored on" and "could / could not evidence" used to print
 * separately, so a bid that failed everything showed the same six names twice and
 * nothing said how weak each was. Each row now carries its own result.
 *
 * A BAR, NOT A MARK. A tick or cross says pass or fail and nothing about how near
 * a miss it was; six crosses in a row read as one undifferentiated failure. The
 * bar shows the average match out of 5, so 2.8 and 0.7 no longer look the same.
 * Fill is pink at the pass level (3) or above and grey below it — grey, not red:
 * a low match is a finding, not an alarm. */
const PASS_LEVEL = 3;

function ScoredCriteria({ rows }) {
  const anyScore = rows.some((r) => r.avg_score != null);
  return (
    <PanelSection label="What it was scored on">
      {anyScore && (
        <p className="-mt-1 mb-4 text-[14px] text-[#71717A]">
          Average match out of 5. {PASS_LEVEL} or more is a pass.
        </p>
      )}
      <ul className="space-y-5">
        {rows.map((r, i) => {
          const has = r.avg_score != null;
          const pass = has ? r.avg_score >= PASS_LEVEL : r.met === true;
          const meta = [
            r.weight != null ? `weight ${r.weight}` : null,
            r.requirements ? `${r.requirements} requirements` : null,
          ].filter(Boolean).join(" · ");
          return (
            <li key={i}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 text-[16px] leading-snug text-[#18181B]">{r.aspect}</span>
                {has ? (
                  <span className="flex-none text-[15.5px] font-medium tabular-nums text-[#3F3F46]">
                    {r.avg_score}
                    <span className="font-normal text-[#71717A]"> / 5</span>
                  </span>
                ) : r.met != null ? (
                  <span className="flex-none text-[14.5px] font-medium text-[#71717A]">
                    {r.met ? "Met" : "Not met"}
                  </span>
                ) : null}
              </div>
              {has && (
                <div
                  role="img"
                  aria-label={`Average match ${r.avg_score} out of 5, ${pass ? "at or above" : "below"} the pass level of ${PASS_LEVEL}`}
                  className="relative mt-2 h-1.5 rounded-full bg-black/[0.06]"
                >
                  <div
                    className={`h-full rounded-full ${pass ? "bg-[#C95EBE]" : "bg-[#A1A1AA]"}`}
                    style={{ width: `${Math.min(Math.max(r.avg_score / 5, 0), 1) * 100}%` }}
                  />
                </div>
              )}
              {meta && <div className="mt-1.5 text-[14.5px] leading-snug text-[#71717A]">{meta}</div>}
            </li>
          );
        })}
      </ul>
    </PanelSection>
  );
}

/* What the RFP asked, and what we could stand behind — frozen on the day.
 *
 * WHY IT IS STORED AT ALL. `readiness_analysis` on the project is a LIVE view:
 * the Knowledge Base grows and the analyser gets re-run, so within weeks it stops
 * describing the bid that was lost and starts describing the company as it is
 * now. The reason code says "capability gap"; this says which capability, on the
 * day it mattered.
 */
function ProjectContext({ c, projectId, orgId }) {
  return (
    <div>
      {(c.verdict || c.score != null) && (
        <div className="mb-5 flex items-center gap-4 rounded-xl border border-[#C95EBE]/25 bg-[#C95EBE]/[0.06] px-4 py-3.5">
          {c.score != null && (
            <span className="text-[30px] font-semibold leading-none tabular-nums text-[#9C3D93]">
              {c.score}
            </span>
          )}
          <div className="min-w-0">
            <div className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">
              Bid Readiness predicted
            </div>
            {c.verdict && <div className="text-[17px] font-semibold text-[#18181B]">{c.verdict}</div>}
          </div>
        </div>
      )}
      {/* Readiness that arrived after the close. Everything else here is frozen
          history; this line is the one thing that is not, and it says so. */}
      {c.readiness_note && (
        <p className="-mt-2 mb-5 text-[15px] leading-relaxed text-[#71717A]">{c.readiness_note}</p>
      )}
      {/* The prediction and what happened disagree — said plainly, because that
          gap is the useful part. */}
      {c.score_note && (
        <p className="-mt-2 mb-5 rounded-lg bg-[#FAFAFA] px-3.5 py-3 text-[15px] leading-relaxed text-[#52525B]">
          {c.score_note}
        </p>
      )}

      <Bullets
        // Everything else in this panel is frozen history. Saying which line is
        // not keeps "what we knew then" from meaning two things in one box.
        label={
          c.asked_for_is_live
            ? "What the RFP asks for · read from the project now, not saved at close"
            : "What the RFP asked for"
        }
        items={c.asked_for}
        // Only twelve are kept, so a long RFP would otherwise look complete.
        // The full list is the project's own Requirements tab.
        footer={
          c.asked_for_total > (c.asked_for?.length || 0) && projectId ? (
            <Link
              to={`/rfp-projects/project/${projectId}${orgId ? `/org/${orgId}` : ""}/requirements`}
              className="mt-3 inline-flex items-center gap-1 text-[15.5px] font-medium text-[#9C3D93] no-underline hover:underline"
            >
              See all {c.asked_for_total} requirements <span aria-hidden="true">→</span>
            </Link>
          ) : null
        }
      />
      {c.scored?.length ? (
        <ScoredCriteria rows={c.scored} />
      ) : (
        <>
          <Bullets
            label="What it was scored on"
            items={(c.asked || []).map((a) =>
              a.weight != null ? `${a.aspect} · weight ${a.weight}` : a.aspect
            )}
          />
          <Bullets label="Could stand behind" items={c.evidenced} marker="✓" />
          <Bullets label="Could not evidence" items={c.not_evidenced} marker="✕" />
        </>
      )}

      {/* The "Gaps, and what asked for them" section was removed here: it is the
          Go/No-Go readiness analysis's own output (the first failed requirements
          it scored), already shown in full on that tab, and as a sample it read
          like a ranking it was not. The gap NAMES are still stored on the bid and
          still drive the "same gap" warnings on other projects. */}

      <Bullets label="Regulations named" items={c.regulations} />

      {/* Without this a panel holding requirements and nothing else reads as
          data withheld. The real reason is invisible from here. */}
      {c.not_captured && (
        <p className="mb-4 rounded-lg bg-[#FAFAFA] px-3.5 py-3 text-[15px] leading-relaxed text-[#71717A]">
          {c.not_captured}
        </p>
      )}

    </div>
  );
}

/* The same right-docked shell the item panels use, for a closed bid's
 * reference material. It opens beside the list instead of unfolding inside a
 * row, so reading one never pushes the next bid down the page. */
function BidDetailPanel({ title, subtitle, badge, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[10000] flex justify-end bg-black/20" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-[520px] flex-col bg-white font-outfit shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} — ${subtitle}`}
      >
        <div className="flex items-start gap-3.5 px-6 py-5">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{title}</div>
            <div className="mt-0.5 text-[18px] font-semibold leading-snug text-[#18181B]">{subtitle}</div>
            {badge && <div className="mt-2">{badge}</div>}
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            className="flex-none rounded-md p-1.5 text-[#71717A] transition-colors hover:bg-black/[0.05] hover:text-[#18181B]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="h-px flex-none bg-black/[0.07]" />
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        <div className="flex flex-none justify-end border-t border-black/[0.07] px-6 py-4">
          <button
            type="button" onClick={onClose}
            className="rounded-lg bg-[#C95EBE] px-4 py-2 text-[15.5px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// Only a win is tinted. The rest stay neutral: a lost or missed bid is a record
// of a decision, not an alarm, and four accent colours on a screen whose job is
// to show what was written down made it read like a dashboard.
const OutcomePill = ({ outcome }) => {
  const tone = /^w[oi]n/i.test(outcome || "")
    ? "border-[#6DBA84] bg-[#E3F4E8] text-[#0B6B24]"
    : /^lost/i.test(outcome || "")
      ? "border-[#E58A8A] bg-[#FDECEC] text-[#A3141B]"
      : "border-[#A1A1AA] bg-[#F1F1F3] text-[#3F3F46]";
  return (
    <span className={`inline-block flex-none whitespace-nowrap rounded-full border px-2.5 py-[1px] text-[12px] font-semibold ${tone}`}>
      {outcome}
    </span>
  );
};

const BidAction = ({ onClick, children }) => (
  <button
    type="button" onClick={onClick} aria-haspopup="dialog"
    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-black/[0.12] px-3 py-1 text-[14.5px] font-medium text-[#3F3F46] transition-colors hover:border-[#C95EBE]/50 hover:bg-[#C95EBE]/[0.05] hover:text-[#9C3D93]"
  >
    {children}
    <span aria-hidden="true">›</span>
  </button>
);

const BID_DETAIL_SOURCE = {
  listing: "from the tender listing",
  rfp: "read from the RFP",
  entered: "from the project",
};

/* Details on a bid that is already closed: buyer type, sector, our price, who
 * won. Added HERE rather than in the close dialog, so closing a project stays a
 * two-click action and the details can be filled in later with the bid in front
 * of you. Same click-to-edit rows as the other panels; each one saves on its own.
 * The outcome itself is never touched. */
function BidDetailsPanel({ bid, orgId, onSaved, onClose }) {
  const [saving, setSaving] = useState(false);
  // Everything the panel needs arrives with the record, so it opens already
  // showing its rows — no request first, no "checking" state.
  const details = bid.details || {};
  const choices = bid.detail_choices || {};
  const prefilled = bid.detail_prefill || {};

  const save = async (key, value) => {
    setSaving(true);
    try {
      const res = await api.post(`${URL}closed-bid/`, {
        org_id: Number(orgId), project_id: bid.project_id, [key]: value,
      });
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "That didn't save.");
    } finally {
      setSaving(false);
    }
  };

  const lost = bid.outcome === "Lost";
  const closedWithPrice = ["Won", "Lost"].includes(bid.outcome);
  const rows = [
    { key: "buyer", label: "Buyer" },
    { key: "buyer_type", label: "Buyer type", choices: choices.buyer_type },
    { key: "sector", label: "Sector", choices: choices.sector },
    ...(closedWithPrice
      ? [
          { key: "our_price", label: "Our price" },
          { key: "date_submitted", label: "Submitted on (YYYY-MM-DD)" },
        ]
      : []),
    ...(lost
      ? [{ key: "winner", label: "Who won?" }, { key: "winning_price", label: "Winning price" }]
      : []),
  ].filter((r) => r.key !== "sector" || r.choices?.length);

  return (
    <BidDetailPanel
      title={Object.keys(details).length ? "Tender listing" : "Add details"}
      subtitle={bid.project || "Untitled project"}
      badge={<OutcomePill outcome={bid.outcome} />}
      onClose={onClose}
    >
      <div className="-mt-1 divide-y divide-black/[0.06]">
        {rows.map((r) => {
          const fromPrefill = prefilled[r.key];
          const value = details[r.key] ?? fromPrefill?.value ?? "";
          // A source is only said for a value nobody has typed over.
          const source = details[r.key] === undefined ? BID_DETAIL_SOURCE[fromPrefill?.source] : null;
          return (
            <div key={r.key}>
              <PanelField
                f={{ key: r.key, label: r.label, value: String(value), editable: true, choices: r.choices || [] }}
                busy={saving}
                onSet={(v) => save(r.key, v)}
              />
              {source && <div className="-mt-2 pb-3 text-[14px] text-[#71717A]">{source}</div>}
            </div>
          );
        })}
      </div>
      <p className="mt-5 text-[14.5px] leading-relaxed text-[#71717A]">
        Optional. These let later questions like “how do we do with this kind of
        buyer” be answered from what was actually written down.
      </p>
    </BidDetailPanel>
  );
}

function ClosedBid({ bid: initial, orgId, onRemoved }) {
  const readOnly = useContext(ReadOnly);
  // Which reference panel is open: "context" | "tender" | "details" | null.
  const [panel, setPanel] = useState(null);
  // The record after a details save replaces the one the page loaded with.
  const [bid, setBid] = useState(initial);
  useEffect(() => setBid(initial), [initial]);

  const facts = [bid.buyer, bid.line_of_work, bid.geography].filter(Boolean);
  const name = bid.project || "Untitled project";
  const d = bid.details || {};
  const added = [
    d.buyer_type,
    d.sector,
    d.our_price && `Our price ${d.our_price}`,
    d.winner && `Lost to ${d.winner}${d.winning_price ? ` (${d.winning_price})` : ""}`,
  ].filter(Boolean);
  const hasDetails = Object.keys(d).length > 0;
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (!(await askConfirm({
      title: `Remove "${name}" from Closed bids?`,
      message: "Its reason, notes and details are deleted from Memory, and it will no longer flag similar bids. The project and its status are not changed.",
    }))) return;
    setRemoving(true);
    try {
      await api.delete(URL, { data: { org_id: Number(orgId), project_id: bid.project_id } });
      onRemoved?.(bid.project_id);
    } catch (e) {
      setRemoving(false);
      toast.error(e?.response?.data?.message || "Couldn't remove this bid.");
    }
  };

  const submitted = d.date_submitted ? fmtDate(d.date_submitted) : null;

  return (
    <div className="group flex min-w-0 flex-col gap-2 rounded-lg border border-[#D4D4D8] bg-white px-4 py-3.5">
      <div className="flex items-center gap-2">
        <OutcomePill outcome={bid.outcome} />
        <span className="flex-1" />
        {submitted && <span className="text-[13px] text-[#3F3F46]">Submitted {submitted}</span>}
        {/* Same hover trash as the other Memory cards, not a button of its own:
            removing is rare, so it stays out of the way until you reach for it. */}
        <span className="flex-none opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <IconBtn danger label={`Remove ${name}`} disabled={removing} onClick={remove}>
            <TrashIcon />
          </IconBtn>
        </span>
      </div>

      <div className="flex flex-col gap-0.5">
        <h3 className="text-[15px] font-medium leading-snug text-[#18181B]">{name}</h3>
        {/* What the RFP was, frozen on the day it closed. */}
        {!!facts.length && <p className="text-[13px] text-[#3F3F46]">{facts.join(" · ")}</p>}
        {/* What a person added afterwards. */}
        {!!added.length && <p className="text-[13px] text-[#52525B]">{added.join(" · ")}</p>}
      </div>

      {!!bid.why?.length && (
        <p className="text-[14px] leading-normal text-[#27272A]">{bid.why.join(". ")}.</p>
      )}

      {bid.note && (
        <p className="border-l-2 border-[#E7C6E3] pl-3 text-[14px] italic leading-normal text-[#52525B]">
          {bid.note}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[#E4E4E7] pt-2">
        {/* Still says whether this bid can reach a later one — the only thing on
            the card a reader cannot work out for themselves. The long form is
            on hover. */}
        <span title={bid.recall_note} className="flex cursor-default items-start gap-1.5 text-[13px] leading-snug text-[#3F3F46]">
          <RefreshCw size={12} className="mt-[3px] flex-none text-[#71717A]" />
          <span>
            {bid.recall_label}
            {bid.recall_why ? ` · ${bid.recall_why}` : ""}
          </span>
        </span>

        <span className="ml-auto flex flex-wrap items-center gap-2">
          {/* An uploaded bid has no listing, so what a person adds IS its
              listing: the button reads "Add details" until something is saved,
              then becomes "Tender listing" like every other bid's. A bid that
              came from Tender Search already has the real one, so it gets no
              second form. */}
          {!bid.tender && (hasDetails || !readOnly) && (
            <BidAction onClick={() => setPanel("details")}>
              {hasDetails ? "Tender listing" : "Add details"}
            </BidAction>
          )}
          {/* Only when something was actually frozen — a project closed before
              anything ran on it has nothing to open. */}
          {bid.context?.has_detail && (
            <BidAction onClick={() => setPanel("context")}>What we knew then</BidAction>
          )}
          {/* Only for a project that came from Tender Search; an uploaded RFP has
              no listing behind it. */}
          {bid.tender && <BidAction onClick={() => setPanel("tender")}>Tender listing</BidAction>}
        </span>
      </div>

      {panel === "details" && (
        <BidDetailsPanel
          bid={bid}
          orgId={orgId}
          onSaved={setBid}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === "context" && bid.context?.has_detail && (
        <BidDetailPanel
          title="What we knew then"
          subtitle={name}
          badge={<OutcomePill outcome={bid.outcome} />}
          onClose={() => setPanel(null)}
        >
          <ProjectContext c={bid.context} projectId={bid.project_id} orgId={orgId} />
        </BidDetailPanel>
      )}
      {panel === "tender" && bid.tender && (
        <BidDetailPanel
          title="Tender listing"
          subtitle={name}
          badge={<OutcomePill outcome={bid.outcome} />}
          onClose={() => setPanel(null)}
        >
          <TenderDetails t={bid.tender} />
        </BidDetailPanel>
      )}
    </div>
  );
}

/* The closed bids as a card grid. No header bar or outcome filter: each card
 * already carries its own outcome, and the tab itself says what this is. */
function ClosedBidsList({ bids, orgId, onRemoved }) {
  if (!bids?.length) {
    return (
      <div className="rounded-lg border border-[#D4D4D8] bg-white px-4 py-5">
        <p className="text-[15px] font-semibold text-[#18181B]">No closed bids yet.</p>
        <p className="mt-1 text-[14px] leading-normal text-[#3F3F46]">
          When a project is closed, its outcome and the reasons behind it are recorded here, so similar bids later can learn from it.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {bids.map((bid) => (
          <ClosedBid key={bid.project_id} bid={bid} orgId={orgId} onRemoved={onRemoved} />
        ))}
      </div>
    </div>
  );
}

/* The same change the server is about to make, applied to the screen now.
   Mirrors the view actions in rfp/views/org_memory.py; the server's response
   replaces this once it lands, so anything approximated here (a new entry's
   fields, a person's headline) is corrected within the round-trip. */
function optimisticEntry(entry, body) {
  const { action, text, item, key, value } = body;
  if (entry.kind !== "list") {
    if (action === "set") return { ...entry, text, edited: true };
    if (action === "clear") return { ...entry, text: "", edited: true };
    return entry;
  }
  if (action === "remove") {
    return { ...entry, edited: true, items: entry.items.filter((i) => i.text !== text) };
  }
  if (action === "add") {
    const t = text.trim().toLowerCase();
    if (entry.items.some((i) => i.text.trim().toLowerCase() === t)) return entry;
    return {
      ...entry, edited: true,
      items: [...entry.items, { key: text, text, origin: "added", detail: {} }],
    };
  }
  if (action === "set_item_field") {
    return {
      ...entry, edited: true,
      items: entry.items.map((i) => (i.key !== item ? i : {
        ...i, patched: true,
        detail: {
          ...i.detail,
          fields: (i.detail?.fields || []).map((f) => (f.key === key ? { ...f, value } : f)),
        },
      })),
    };
  }
  return entry;
}

function applyOptimistic(data, body) {
  if (!data?.sections) return data;
  return {
    ...data,
    sections: data.sections.map((s) => ({
      ...s,
      entries: s.entries.map((e) => (e.field === body.field ? optimisticEntry(e, body) : e)),
    })),
  };
}

const OrganizationMemory = ({ isReviewer = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const orgId = Cookies.get("selected_org_id");

  const load = useCallback(async () => {
    if (!orgId) { setError("No workspace selected."); setLoading(false); return; }
    try {
      const res = await api.get(`${URL}?org_id=${orgId}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't load this workspace's memory.");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // OPTIMISTIC, THEN CONFIRMED. A change shows the moment it's made and the
  // request goes out behind it, instead of the screen waiting a full
  // round-trip (save + cache invalidation + rebuilding the whole view) first.
  //
  // Requests run ONE AT A TIME, in order: each is a read-modify-write of the
  // same overrides row, so two in flight together could silently drop one.
  // Only the LAST response is applied — an earlier one predates the edits
  // still queued behind it and would briefly undo them. If a request fails,
  // the rest of that burst is skipped and the screen reloads from the server,
  // so it never keeps showing a change that didn't save.
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef(0);
  const failedRef = useRef(false);

  const mutate = useCallback((body) => {
    setData((prev) => applyOptimistic(prev, body));
    pendingRef.current += 1;
    setSaving(true);
    const request = queueRef.current.then(async () => {
      if (failedRef.current) return null;
      try {
        const res = await api.post(URL, { org_id: Number(orgId), ...body });
        return res.data;
      } catch (e) {
        failedRef.current = true;
        toast.error(e?.response?.data?.message || "That change didn't save.");
        return null;
      }
    });
    queueRef.current = request;
    return request.then((serverData) => {
      pendingRef.current -= 1;
      if (pendingRef.current === 0) {
        setSaving(false);
        if (failedRef.current) {
          failedRef.current = false;
          load();
        } else if (serverData) {
          setData(serverData);
        }
      }
      return serverData;
    });
  }, [orgId, load]);

  // SILENT — no `loading`/`busy` flag. This runs every REBUILD_POLL_MS while a
  // rebuild is in flight; flipping the full-page skeleton or disabling the
  // screen on every tick would make an already multi-minute wait feel worse,
  // not better. `load()` already tolerates being called again mid-session.
  useEffect(() => {
    if (!data?.rebuild_in_progress) return undefined;
    const id = setInterval(load, REBUILD_POLL_MS);
    return () => clearInterval(id);
  }, [data?.rebuild_in_progress, load]);

  /* THREE REBUILD STATES, NOT ONE BOOLEAN.
   *
   * `rebuild_in_progress` alone can't tell "actively running, back in a few
   * minutes" from "queued, still waiting out a settle window that can run to
   * five minutes before it even starts" — and showing the same indefinite
   * "Building your memory…" spinner for both is what made the wait after an
   * upload feel stuck: nothing to look at, no number, no way to act on it.
   *
   * `rebuild_active` (a live build lock) gets the existing spinner treatment —
   * it's a bounded, genuinely-running ~150s operation. A pending key with no
   * lock yet is either the automatic settle window (its value is a real
   * future timestamp) or a "Sync now" click about to be picked up by a worker
   * (its value is ~now) — the latter is treated the same as active since
   * there's nothing meaningful to wait out.
   */
  const etaMs = data?.rebuild_eta ? new Date(data.rebuild_eta).getTime() : null;
  const etaIsFuture = !!(etaMs && etaMs > Date.now() + 5000);
  const rebuildActive = !!data?.rebuild_active;
  const rebuildScheduled = !!data?.rebuild_in_progress && !rebuildActive && etaIsFuture;
  const showBuildingUI = rebuildActive || (!!data?.rebuild_in_progress && !etaIsFuture);
  const rebuildEtaLabel = etaMs
    ? new Date(etaMs).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : null;
  // Nothing to sync: no pending/running rebuild and the profile isn't stale
  // (no upload or delete since the last successful build, and no reset since
  // either). A button that does nothing useful is worse than no button.
  const canSync = !!(data?.is_stale || data?.rebuild_in_progress);

  // No confirm dialog — unlike reset, this can't lose anything. Worst case is
  // a redundant rebuild if the profile was already current.
  const [syncing, setSyncing] = useState(false);
  const syncMemory = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await api.post(SYNC_URL, { org_id: Number(orgId) });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't start a sync.");
    } finally {
      // Not tied to how long the actual rebuild takes — this just covers the
      // request itself. The screen already shows "Building memory…" and
      // polls until rebuild_in_progress (in the response above) goes false.
      setSyncing(false);
    }
  }, [orgId]);

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const resetMemory = useCallback(async () => {
    setResetting(true);
    try {
      const res = await api.post(RESET_URL, { org_id: Number(orgId) });
      setData(res.data);
      setShowResetConfirm(false);
      toast.success("Memory reset. It rebuilds the next time a document is added.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't reset memory.");
    } finally {
      setResetting(false);
    }
  }, [orgId]);

  /* The switchable groups. Built from whatever the server actually returned
     rather than a hardcoded list, so a section added to SECTIONS server-side
     appears here without a frontend change.

     Profile groups are dropped when the profile is empty: every declared field
     renders a row now, so those tabs would offer five screens of blank rows, and
     the empty-state card below already says what to do about it. */
  const tabs = useMemo(() => {
    if (!data) return [];
    const out = data.is_empty
      // One tab holding the "nothing here yet" card, rather than none. Without
      // it a workspace with closed bids and no profile would show that card
      // directly above the bids it contradicts — or, if it were simply hidden,
      // would never say the profile is missing at all.
      ? [{ key: "profile", title: "Company",
           hint: "Read from your Knowledge Base" }]
      : (data.sections || []).map((s) => ({
          key: s.key, title: s.title, hint: s.hint,
        }));
    // Always listed, as in the design, so people know where outcomes will appear.
    out.push({
      key: "closed",
      title: "Closed bids",
      hint: "What we recorded when a project was closed",
      count: data.closed_bids?.length || 0,
    });
    return out;
  }, [data]);

  const [tab, setTab] = useState(null);
  // DERIVED, not synced. An effect keeping `tab` inside `tabs` would render one
  // frame on a tab that no longer exists; falling back to the first is enough.
  const active = tabs.find((t) => t.key === tab) || tabs[0];
  // The left nav shows only when there is more than one section to move between.
  const showNav = !loading && !error && tabs.length > 1;
  // A labelled region, not a tabpanel — the left list is site navigation now,
  // not a tab widget.
  const panelProps = active ? { role: "region", "aria-label": active.title } : {};

  // The active section's cards, filtered by the search box. Empty rows drop out
  // while searching, and a card with nothing left is hidden.
  const cards = useMemo(() => {
    if (!data || data.is_empty) return [];
    const section = (data.sections || []).find((s) => s.key === active?.key);
    if (!section) return [];
    const raw = toCards(section.entries);
    const query = q.trim().toLowerCase();
    if (!query) return raw;
    const hit = (e) =>
      (e.label + " " + (e.text || "") + " " +
        (e.items || []).map((i) => i.text).join(" "))
        .toLowerCase().includes(query);
    return raw
      .map((c) => ({ ...c, entries: c.entries.filter(hit) }))
      .filter((c) => c.entries.length);
  }, [data, active?.key, q]);

  const builtLabel = data?.built_at
    ? new Date(data.built_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;
  const statusLabel = saving
    ? "Saving…"
    : showBuildingUI
      ? "Building memory…"
      : rebuildScheduled
        ? "Sync scheduled"
        : data?.is_stale
          ? builtLabel ? "New documents to read" : "Not built yet"
          : "Up to date";
  const statusSub = showBuildingUI
    ? "Reading what was just added"
    : rebuildScheduled
      ? `Syncs on its own at ${rebuildEtaLabel}`
      : builtLabel
        ? `Last built ${builtLabel}`
        : "Builds after a document is added";

  const docTabs = tabs.filter((t) => t.key !== "closed");
  const closedTab = tabs.find((t) => t.key === "closed");
  const navItem = (t) => {
    const on = t.key === active?.key;
    return (
      <button
        key={t.key}
        type="button"
        aria-current={on ? "page" : undefined}
        onClick={() => setTab(t.key)}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-[7px] text-left transition-colors ${
          on ? "bg-[#8B55DD]/[0.12] text-[#6F35C4]" : "text-[#27272A] hover:bg-[#F1EAF4]"
        }`}
      >
        <span className={`min-w-0 flex-1 truncate text-[14px] ${on ? "font-semibold" : ""}`}>{t.title}</span>
        {t.count != null && (
          <span className={`flex-none text-[12px] font-semibold tabular-nums ${on ? "text-[#6F35C4]" : "text-[#3F3F46]"}`}>
            {t.count}
          </span>
        )}
      </button>
    );
  };
  const navGroup = (label, items) => (
    <div>
      <div className="px-2 py-1.5 text-[12px] font-semibold uppercase tracking-[0.8px] text-[#A04A97]">{label}</div>
      <div className="relative ml-[17px] mt-1 flex flex-col gap-px pl-1.5">
        <span aria-hidden className="absolute bottom-0.5 left-0 top-0.5 w-[1.5px] bg-[#E4E4E7]" />
        {items.map(navItem)}
      </div>
    </div>
  );

  // While a sync is scheduled or running the banner carries "Sync now instead", so no second button here.
  const showSyncBtn = !!(canSync && !data?.rebuild_in_progress);
  const statusBox = data && (
    <div className="flex flex-col gap-2.5 rounded-lg border border-[#D4D4D8] bg-white p-3">
      {/* role="status": this line changes on its own while a rebuild runs
          (polled), so screen readers need it announced, not re-found. */}
      <div role="status" className="flex items-center gap-2">
        {saving || showBuildingUI ? (
          <Loader2 size={13} className="flex-none animate-spin text-[#A04A97]" />
        ) : (
          <span className={`h-2 w-2 flex-none rounded-full ${data.is_stale ? "bg-[#D9B53A]" : "bg-[#2DAD4E]"}`} />
        )}
        <span className="text-[14px] font-medium text-[#18181B]">{statusLabel}</span>
      </div>
      <span className="text-[12px] leading-snug text-[#3F3F46]">{statusSub}</span>
      <div className="flex gap-2">
        {showSyncBtn && (
          <ReviewerActionGate active={isReviewer} block place="top">
            <button
              type="button"
              onClick={syncMemory}
              disabled={saving || syncing || data.rebuild_in_progress}
              title="Rebuild right now, instead of waiting for it to happen on its own"
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-[#A04A97] bg-white px-2.5 py-1.5 text-[13px] font-semibold text-[#A04A97] transition-colors hover:bg-[#FDF2F8] disabled:opacity-40"
            >
              <RefreshCw size={13} className={`flex-none ${syncing ? "animate-spin" : ""}`} />
              Sync memory
            </button>
          </ReviewerActionGate>
        )}
        <ReviewerActionGate active={isReviewer} block={!showSyncBtn} place="top">
          <button
            type="button"
            onClick={() => setShowResetConfirm(true)}
            disabled={saving || data.rebuild_in_progress}
            title="Erase this profile so it rebuilds fresh from your Knowledge Base"
            className={`flex items-center justify-center gap-1.5 rounded-md border border-[#A1A1AA] bg-white px-2.5 py-1.5 text-[13px] font-medium text-[#27272A] transition-colors hover:border-[#71717A] disabled:opacity-40 ${showSyncBtn ? "" : "w-full"}`}
          >
            <RotateCcw size={13} className="flex-none" />
            Reset
          </button>
        </ReviewerActionGate>
      </div>
    </div>
  );

  const banner = "mb-4 flex items-center gap-3 rounded-lg border border-[#E7C6E3] bg-[#FBF3FA] px-3.5 py-2.5";

  return (
    /* Centred, and with no page title: the tab above already says Memory, and a
       heading repeating it pushes the content it labels further down the page. */
    /* reducedMotion="user": an OS-level "reduce motion" preference switches
       every animation in this tree off in one place, with no per-site checks
       to forget. */
    <ReadOnly.Provider value={isReviewer}>
    <MotionConfig reducedMotion="user">
    <div className="mx-auto flex h-full w-full max-w-[1400px] gap-6 font-outfit">
      {/* LEFT NAV, one section at a time. The section a person is reading stays
          highlighted; its own one-line hint becomes the page subtitle, so the
          header always says what this particular group is for. */}
      {showNav && (
        // The nav and the content each scroll on their own, so the divider stays full height and still.
        <div className="h-full w-[248px] flex-none overflow-y-auto border-r border-[#D4D4D8] pb-6 pr-4">
          <nav aria-label="Memory sections" className="flex flex-col gap-3 pt-1">
          {/* Search at the top of the nav. */}
          {data && !data.is_empty && (
            <label className="flex h-[34px] items-center gap-2 rounded-md border border-[#A1A1AA] bg-white px-3 text-[14px] text-[#18181B] transition-colors focus-within:border-[#C95EBE] focus-within:shadow-[0_0_0_1px_#C95EBE]">
              <Search size={14} className="flex-none text-[#71717A]" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search memory"
                aria-label="Search memory"
                className="w-full min-w-0 bg-transparent outline-none placeholder:text-[#A1A1AA]"
              />
            </label>
          )}

          {!!docTabs.length && navGroup("Read from your documents", docTabs)}
          {closedTab && navGroup("From closed projects", [closedTab])}

          {statusBox}
          </nav>
        </div>
      )}

      <div className="h-full min-w-0 flex-1 overflow-y-auto pb-6 pr-1 pt-1">
        {!loading && !error && active && (
          <div className="mb-3 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <h2 className="text-[18px] font-semibold text-[#18181B]">{active.title}</h2>
            {active.hint && <span className="text-[14px] text-[#3F3F46]">{active.hint}</span>}
          </div>
        )}

      {/* Kept OUTSIDE the empty-state guard below — this fires whether the
          profile is empty (the common case, a first document just landed) or
          already has content (a later upload is rebuilding it), so the same
          "why is it changing" question gets one answer either way instead of
          two different messages depending on what happened to already be
          there. */}
      {!loading && !error && showBuildingUI && !data?.is_empty && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className={banner}>
          <Loader2 size={15} className="flex-none animate-spin text-[#A04A97]" />
          <p className="text-[14px] leading-snug text-[#5B2456]">
            Updating memory with what you just added. This can take a few
            minutes, and the page refreshes on its own when it's done.
          </p>
        </motion.div>
      )}

      {/* Still in the settle window — nothing is running yet, so a spinner
          would be a lie. Says exactly when it fires on its own, and offers
          the one thing worth doing about it. */}
      {!loading && !error && rebuildScheduled && !data?.is_empty && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className={banner}>
          <RefreshCw size={15} className="flex-none text-[#A04A97]" />
          <p className="min-w-0 flex-1 text-[14px] leading-snug text-[#5B2456]">
            Memory will sync automatically at{" "}
            <span className="font-medium">{rebuildEtaLabel}</span>
            {" "}with what you just added.
          </p>
          <button
            type="button"
            onClick={syncMemory}
            disabled={saving || syncing}
            className="flex-none whitespace-nowrap rounded-md border border-[#A04A97] bg-white px-3 py-[5px] text-[13px] font-semibold text-[#A04A97] transition-colors hover:bg-[#FDF2F8] disabled:opacity-40"
          >
            Sync now instead
          </button>
        </motion.div>
      )}

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-[42px] animate-pulse rounded-lg bg-[#F4F4F5]" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-[#B91C1C]/20 bg-[#B91C1C]/[0.03] px-4 py-3 text-[16.5px] text-[#B91C1C]">
          {error}
        </div>
      )}

      {/* An empty screen has THREE different causes needing different advice.
          A build actively in flight takes priority over build_status, which
          otherwise still reads whatever the LAST attempt left behind (e.g.
          "error" from before a fresh document was added) — telling someone
          to add a document they just added, or blaming a failure that a
          rebuild already superseded, is worse than saying nothing. */}
      {!loading && !error && data?.is_empty && active?.key === "profile" && (
        <div {...panelProps} className="rounded-lg border border-[#D4D4D8] bg-white px-4 py-5">
          {showBuildingUI ? (
            <>
              <p className="flex items-center gap-2 text-[15px] font-semibold text-[#18181B]">
                <Loader2 size={17} className="flex-none animate-spin text-[#C95EBE]" />
                Building your memory…
              </p>
              <p className="mt-1 text-[14px] leading-normal text-[#3F3F46]">
                Reading what you just added — a multi-field pass over your
                Knowledge Base, usually a few minutes for a full profile. No
                need to refresh; this fills in on its own once it's done.
              </p>
            </>
          ) : rebuildScheduled ? (
            <>
              <p className="text-[15px] font-semibold text-[#18181B]">
                Memory sync scheduled for {rebuildEtaLabel}.
              </p>
              <p className="mt-1 text-[14px] leading-normal text-[#3F3F46]">
                We're giving anything else you're adding right now a chance to
                land first, so one build covers all of it instead of several.
                Don't want to wait?{" "}
                <button
                  type="button"
                  onClick={syncMemory}
                  disabled={saving || syncing}
                  className="font-medium text-[#C95EBE] hover:opacity-75 disabled:opacity-40"
                >
                  Sync now
                </button>
                .
              </p>
            </>
          ) : data.build_status === "error" ? (
            <>
              <p className="text-[15px] font-semibold text-[#18181B]">
                Reading your documents didn&apos;t finish.
              </p>
              <p className="mt-1 text-[14px] leading-normal text-[#3F3F46]">
                Nothing was extracted, so proposals are being written without any
                company facts behind them. Adding another document to the Knowledge
                Base retries it.
              </p>
              {data.build_error && (
                <p className="mt-2 text-[13px] text-[#71717A]">{data.build_error}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-[15px] font-semibold text-[#18181B]">Nothing here yet.</p>
              <p className="mt-1 text-[14px] leading-normal text-[#3F3F46]">
                Add documents to your Knowledge Base — a company profile, past
                proposals, certificates — and this fills in shortly after they
                finish processing.
              </p>
            </>
          )}
        </div>
      )}

      {!loading && !error && data && !data.is_empty && active?.key !== "closed" && (
        <motion.div key={active?.key} {...RISE} {...panelProps}>
          {cards.length === 0 ? (
            <p className="px-6 py-12 text-center text-[15px] text-[#3F3F46]">
              {q.trim()
                ? `Nothing in ${active?.title || "this section"} matches "${q.trim()}".`
                : "Nothing here yet."}
            </p>
          ) : (
            cards.map((card) =>
              card.kind === "list" ? (
                card.entries.map((entry) => (
                  <ListRow
                    key={entry.field}
                    entry={entry}
                    orgId={orgId}
                    onAdd={(text) => mutate({ field: entry.field, action: "add", text })}
                    onRemove={(text) => mutate({ field: entry.field, action: "remove", text })}
                    onSetField={(item, key, value) =>
                      mutate({ field: entry.field, action: "set_item_field", item, key, value })
                    }
                    onEdit={(oldText, newText) => {
                      // An edit is a remove then an add — the same two calls the
                      // server stores, so a hand-made correction and an edit
                      // leave identical state. Both go out at once: the queue
                      // in mutate keeps them in order, and skips the add if the
                      // remove fails.
                      mutate({ field: entry.field, action: "remove", text: oldText });
                      mutate({ field: entry.field, action: "add", text: newText });
                    }}
                  />
                ))
              ) : (
                <MemoryCard key={card.key} title={card.title}>
                  {card.entries.map((entry) => (
                    <FactCell
                      key={entry.field}
                      entry={entry}
                      onSet={(text) => mutate({ field: entry.field, action: "set", text })}
                      onClear={() => mutate({ field: entry.field, action: "clear" })}
                    />
                  ))}
                </MemoryCard>
              )
            )
          )}
        </motion.div>
      )}

      {/* OUTSIDE the is_empty guard on purpose. A workspace can have closed bids
          and no profile at all — that is the common state early on — and hiding
          this behind "nothing here yet" would hide the only memory it has. */}
      {!loading && !error && active?.key === "closed" && (
        <motion.section key="closed" {...RISE} {...panelProps} className={data.is_empty ? "mt-6" : ""}>
          <ClosedBidsList
            bids={data.closed_bids}
            orgId={orgId}
            // Dropped locally so the card and the tab's count update at once,
            // with no reload.
            onRemoved={(id) =>
              setData((d) => ({ ...d, closed_bids: d.closed_bids.filter((b) => b.project_id !== id) }))
            }
          />

        </motion.section>
      )}

      <ConfirmHost />

      {showResetConfirm && (
        <ConfirmDialog
          title="Reset this workspace's memory?"
          message="This erases everything read from your Knowledge Base — capabilities, projects, team, company details, all of it. It rebuilds automatically the next time a document is added; it does not happen on its own from what's already there."
          tip="This also clears anything you added or corrected by hand. It can't be undone."
          confirmLabel="Reset memory"
          danger
          busy={resetting}
          onConfirm={resetMemory}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}
      </div>
    </div>
    </MotionConfig>
    </ReadOnly.Provider>
  );
};

export default OrganizationMemory;
